const GENERIC_TRANSACTION_DESCRIPTIONS = new Set(["transaction", "operation"])
const INTERNAL_TRANSFER_ENTRY_COUNT = 2

export type TransactionListItem<T extends ICompanyTrxTransaction> =
	| { kind: "transaction"; key: string; transaction: T; ledgerEntries: [T] }
	| { kind: "internal-transfer"; key: string; transaction: T; ledgerEntries: [T, T] }

const DEFAULT_TRANSACTION_TYPE_LABELS: Record<TCompanyTransactionType, string> = {
	spent: "Spent",
	returned: "Returned",
	commission: "Commission",
	refund: "Refund",
	deposit: "Deposit",
	other: "Other"
}

const FEE_LABELS: Record<string, string> = {
	card_issue: "Card issuance fee",
	recharge: "Top-up fee",
	crypto_topup: "Crypto deposit fee",
	authorization: "Authorization fee",
	decline: "Decline fee",
	reversal: "Reversal fee",
	refund: "Refund fee",
	cross_border: "Cross-border fee",
	apple_pay: "Apple Pay service fee",
	dispute: "Dispute fee",
	processing: "Processing fee",
	card_price: "Card price (issuer)",
	issue_deduction: "Issuer fee at card issue",
	issuer_fee: "Issuer fee"
}

/** The backend `label` is the source of truth; the local dictionary is a fallback. Unknown codes are shown as-is. */
export const getFeeLabel = (feeCode: string, label?: string | null) => label?.trim() || FEE_LABELS[feeCode] || feeCode

export const ISSUER_FEE_NOTE = "Issuer fee"
export const COMPANY_PAYER_NOTE = "Charged to company balance"
export const PRIVATE_PAYER_NOTE = "Charged to your balance"

/** Who paid a fee. Private accounts have one balance, so company and buyer both read as "your balance". */
export const getFeePayerLabel = (payer: string, privateView = false) => {
	if (payer === "card") return "Card"
	if (privateView) return PRIVATE_PAYER_NOTE
	return payer === "company" ? COMPANY_PAYER_NOTE : "Buyer"
}

const PARENT_TYPE_LABELS: Record<string, string> = {
	auth: "Payment",
	verification: "Card verification",
	void: "Authorization void",
	refund: "Refund",
	topup: "Top-up"
}
export const getParentTypeLabel = (type: string) => PARENT_TYPE_LABELS[type] ?? type

/** Marker lines under a commission row; empty when the row has no charge. `charge.source` is never shown. */
export const getFeeNotes = (transaction: ICompanyTrxTransaction, privateView = false) => {
	const { charge, parent } = transaction
	if (!charge) return []
	const notes: string[] = []
	if (charge.charged_by === "issuer") notes.push(ISSUER_FEE_NOTE)
	if (charge.payer === "company") notes.push(privateView ? PRIVATE_PAYER_NOTE : COMPANY_PAYER_NOTE)
	const subject = cleanText(parent?.merchant) || (parent?.type ? getParentTypeLabel(parent.type) : "")
	const when = parent?.transaction_time ? new Date(parent.transaction_time) : null
	const date = when && !Number.isNaN(when.getTime())
		? when.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
		: ""
	if (subject || date) notes.push(["for", subject, date && `on ${date}`].filter(Boolean).join(" "))
	return notes
}

const cleanText = (value?: string | null) => value?.trim() || ""

/** Private views keep account numbers out of the text; they live in the tooltip and the account popup. */
const getAccountLabel = (accountId: number, context?: string, privateView = false) => {
	if (privateView && context) return context
	const account = Number.isFinite(accountId) && accountId > 0 ? `Account #${accountId}` : "Unknown account"
	return context ? `${context} · ${account}` : account
}

const getOwnedAccountContext = (transaction: ICompanyTrxTransaction, privateView: boolean) => {
	const cardMask = cleanText(transaction.card_mask)
	if (transaction.card_name || cardMask) {
		return [cleanText(transaction.card_name) || "Card", cardMask].filter(Boolean).join(" ")
	}
	return privateView ? "Your account" : cleanText(transaction.buyer_name) || cleanText(transaction.team_name)
}

const getPartyContext = (party: ICompanyTransactionParty, privateView: boolean) => {
	if (party.kind === "card") {
		return [cleanText(party.name) || "Card", cleanText(party.card_mask)]
			.filter(Boolean)
			.join(" ")
	}
	if (party.kind === "merchant") return cleanText(party.name) || "Merchant"
	if (party.kind === "external") return cleanText(party.name) || "External account"
	if (privateView) return "Your account"
	if (party.kind === "company") return cleanText(party.name) || "Company"
	// `party.email` belongs to the internal TRX account and may contain entity
	// identifiers. User-facing labels rely only on the explicit entity name/kind.
	return cleanText(party.name) || "Buyer"
}

export const getTransactionPartyLabel = (party: ICompanyTransactionParty, privateView = false) =>
	getAccountLabel(Number(party.account_id), getPartyContext(party, privateView), privateView)

export const getTransactionTypeLabel = (
	transaction: ICompanyTrxTransaction,
	typeOption?: ICompanyTransactionFilterOption
) => {
	const backendLabel = cleanText(typeOption?.label)
	if (backendLabel) return backendLabel
	if (transaction.operation_type) {
		return DEFAULT_TRANSACTION_TYPE_LABELS[transaction.operation_type] ??
			transaction.operation_type.replaceAll("_", " ")
	}
	return Number.isFinite(transaction.type_id) ? `Type #${transaction.type_id}` : "Unknown type"
}

// Backend text from WithdrawService: "Withdrawal from card uid: <uid> to company|buyer".
const CARD_WITHDRAWAL_DESCRIPTION = /^Withdrawal from card uid: \S+ to (company|buyer)$/i

export const getTransactionDescription = (
	transaction: ICompanyTrxTransaction,
	typeOption?: ICompanyTransactionFilterOption,
	privateView = false
) => {
	if (transaction.charge?.fee_code) return getFeeLabel(transaction.charge.fee_code, transaction.charge.label)
	const description = cleanText(transaction.description)
	if (privateView && CARD_WITHDRAWAL_DESCRIPTION.test(description)) return "Withdrawal from card to your balance"
	if (description && !GENERIC_TRANSACTION_DESCRIPTIONS.has(description.toLowerCase())) return description

	const parentDescription = cleanText(transaction.parent?.description)
	if (parentDescription && !GENERIC_TRANSACTION_DESCRIPTIONS.has(parentDescription.toLowerCase())) {
		return parentDescription
	}

	return cleanText(typeOption?.details) || getTransactionTypeLabel(transaction, typeOption)
}

export const getTransactionDisplayLabel = (
	transaction: ICompanyTrxTransaction,
	typeOption?: ICompanyTransactionFilterOption,
	privateView = false
) => transaction.charge?.fee_code
	? getFeeLabel(transaction.charge.fee_code, transaction.charge.label)
	: cleanText(transaction.merchant?.name) || getTransactionDescription(transaction, typeOption, privateView)

export const formatMerchantWallet = (wallet: string) => {
	const normalized = cleanText(wallet).toLowerCase()
	if (normalized === "applepay") return "Apple Pay"
	if (normalized === "googlepay") return "Google Pay"
	return wallet
}

export const getTransactionParties = (transaction: ICompanyTrxTransaction, privateView = false) => {
	if (transaction.from && transaction.to) {
		return {
			from: getTransactionPartyLabel(transaction.from, privateView),
			to: getTransactionPartyLabel(transaction.to, privateView)
		}
	}
	const ownedAccount = getAccountLabel(transaction.account_id, getOwnedAccountContext(transaction, privateView), privateView)
	const counterparty = getAccountLabel(transaction.counterparty_account_id)

	return transaction.direction === "income"
		? { from: counterparty, to: ownedAccount }
		: { from: ownedAccount, to: counterparty }
}

export const getTransactionAmountPresentation = (transaction: ICompanyTrxTransaction) => ({
	color: transaction.direction === "income" ? "#067647" : "#DC2626",
	sign: transaction.direction === "income" ? "+" : "−",
	amount: Math.abs(Number(transaction.amount) || 0)
})

const getTransactionRouteKey = (transaction: ICompanyTrxTransaction) => {
	if (transaction.from?.account_id && transaction.to?.account_id) {
		return `${transaction.from.account_id}->${transaction.to.account_id}`
	}

	return [transaction.account_id, transaction.counterparty_account_id]
		.map(Number)
		.sort((left, right) => left - right)
		.join(":")
}

const isInternalTransferPair = <T extends ICompanyTrxTransaction>(entries: T[]): entries is [T, T] => {
	if (entries.length !== INTERNAL_TRANSFER_ENTRY_COUNT) return false
	const [first, second] = entries
	if (first.operation_id === null || first.operation_id === undefined) return false

	return first.direction !== second.direction &&
		first.status === second.status &&
		first.currency === second.currency &&
		Math.abs(Number(first.amount) || 0) === Math.abs(Number(second.amount) || 0) &&
		getTransactionRouteKey(first) === getTransactionRouteKey(second)
}

/**
 * Collapses the two account-side ledger entries of one internal transfer into a
 * single user-facing operation. Groups with fees, partial data, or more than
 * two entries remain untouched so their accounting meaning is not hidden.
 */
export const groupInternalTransfers = <T extends ICompanyTrxTransaction & { uid: string }>(
	transactions: T[]
): TransactionListItem<T>[] => {
	const byOperationId = new Map<number, T[]>()
	transactions.forEach((transaction) => {
		if (transaction.operation_id === null || transaction.operation_id === undefined) return
		const entries = byOperationId.get(transaction.operation_id) ?? []
		entries.push(transaction)
		byOperationId.set(transaction.operation_id, entries)
	})

	const consumed = new Set<string>()
	const result: TransactionListItem<T>[] = []

	transactions.forEach((transaction) => {
		if (consumed.has(transaction.uid)) return
		const entries = transaction.operation_id === null || transaction.operation_id === undefined
			? []
			: byOperationId.get(transaction.operation_id) ?? []

		if (isInternalTransferPair(entries)) {
			entries.forEach((entry) => consumed.add(entry.uid))
			const representative = entries.find((entry) => entry.direction === "income" && entry.buyer_name) ??
				entries.find((entry) => entry.direction === "expense") ?? entries[0]
			result.push({
				kind: "internal-transfer",
				key: `transfer-${transaction.operation_id}`,
				transaction: representative,
				ledgerEntries: entries
			})
			return
		}

		consumed.add(transaction.uid)
		result.push({ kind: "transaction", key: transaction.uid, transaction, ledgerEntries: [transaction] })
	})

	return result
}
